import {
  getRecentCallsQueryOptions,
  type AgentCall,
} from "@/api/get-agent-calls";
import { cn } from "@/lib/utils";
import { ConversationTimeDisplay } from "@rulebase/core/components";
import { useQuery } from "@tanstack/react-query";
import { IconCall } from "central-icons";
import type { Components } from "react-markdown";
import { Skeleton } from "./ui/skeleton";
import { InitialsAvatar } from "./ui/initials-avatar";
import { Markdown } from "./ui/markdown";

const RECENT_CALLS_LIMIT = 3;

/**
 * Summaries are markdown, but a row is one line: collapsing every block to an
 * inline box keeps its emphasis and lists readable without the syntax, and lets
 * the row truncate the whole thing as a single run of text.
 */
const INLINE_MARKDOWN_CLASS =
  "[&_h1]:mr-1 [&_h1]:inline [&_h2]:mr-1 [&_h2]:inline [&_h3]:mr-1 " +
  "[&_h3]:inline [&_h4]:mr-1 [&_h4]:inline [&_li]:mr-1 [&_li]:inline " +
  "[&_ol]:inline [&_p]:mr-1 [&_p]:inline [&_pre]:inline [&_ul]:inline " +
  "[&_h1]:text-inherit [&_h2]:text-inherit [&_h3]:text-inherit " +
  "[&_h4]:text-inherit [&_:is(h1,h2,h3,h4,strong)]:font-medium";

const INLINE_MARKDOWN_COMPONENTS: Partial<Components> = {
  // The row is a button, so an anchor inside it would be invalid nesting, and a
  // highlighted code block would break the line. Both keep their text only; the
  // call screen renders the summary in full.
  a: function InlineLink(props) {
    return <span>{props.children}</span>;
  },
  code: function InlineCode(props) {
    return <span className="font-mono">{props.children}</span>;
  },
  pre: function InlinePre(props) {
    return <>{props.children}</>;
  },
};

/**
 * The agent's own recent calls across tickets, one line each. Opening a row
 * hands off to the call screen, where the full summary and transcript live.
 */
export function RecentCallsView(props: {
  onOpenCall: (callId: string) => void;
}) {
  const { data, isPending } = useQuery(
    getRecentCallsQueryOptions({
      limit: RECENT_CALLS_LIMIT,
    }),
  );

  if (isPending) {
    return (
      <Section>
        <Skeleton className="h-24 w-full" />
      </Section>
    );
  }

  const calls = data?.data ?? [];
  if (calls.length === 0) return null;

  return (
    <Section>
      <div className="flex flex-col gap-1">
        {calls.map((call) => (
          <CallRow
            key={call.id}
            call={call}
            onOpen={() => props.onOpenCall(call.id)}
          />
        ))}
      </div>
    </Section>
  );
}

function Section(props: { children: React.ReactNode }) {
  return (
    <div className="mt-5 flex w-full flex-col gap-1.5 border-t border-neutral-200 pt-4">
      <h2 className="flex items-center gap-1.5 font-medium text-neutral-700">
        <IconCall aria-hidden className="size-4 shrink-0 text-neutral-500" />
        Your recent calls
      </h2>
      {props.children}
    </div>
  );
}

/**
 * The name sits in a fixed lane so every summary starts at the same x; without
 * it the summaries step in and out with each name's length. The row's negative
 * inline margin cancels its own padding, so the hover tint bleeds past the
 * content edge while the text stays put.
 */
function CallRow(props: { call: AgentCall; onOpen: () => void }) {
  const { call } = props;

  return (
    <button
      type="button"
      onClick={props.onOpen}
      className="-mx-2 flex w-[calc(100%+1rem)] cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-neutral-100"
    >
      <InitialsAvatar name={call.customer_name} />

      <span className="w-[116px] shrink-0 truncate font-medium text-neutral-700">
        {call.customer_name ?? "Unknown caller"}
      </span>

      {call.summary ? (
        <Markdown
          className={cn(
            "min-w-0 flex-1 truncate text-neutral-500",
            INLINE_MARKDOWN_CLASS,
          )}
          components={INLINE_MARKDOWN_COMPONENTS}
        >
          {call.summary}
        </Markdown>
      ) : (
        <span className="min-w-0 flex-1 truncate text-neutral-500">
          Summary not ready yet
        </span>
      )}

      {call.started_at && (
        <ConversationTimeDisplay
          date={call.started_at}
          // min-width keeps short dates in a lane; nowrap lets a longer one
          // grow instead of wrapping, and the summary truncates to make room.
          className="min-w-14 shrink-0 whitespace-nowrap text-right text-neutral-500"
        />
      )}
    </button>
  );
}
